import { useCallback, useEffect, useState } from "react";
import {
  collectionGroup,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "../../firebase/firebase.js";
import styles from "./Admin.module.css";

function formatWhen(value) {
  if (!value) return "—";
  const date = value?.toDate?.() || new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB");
}

export default function AdminComments() {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const loadPending = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const q = query(
        collectionGroup(db, "comments"),
        where("status", "==", "pending")
      );
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => {
        const studentId = d.ref.parent.parent?.id || "";
        return {
          id: d.id,
          path: d.ref.path,
          studentId,
          ...d.data(),
        };
      });
      rows.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
      setComments(rows);
    } catch (err) {
      setError(err.message || "Failed to load pending comments");
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  const moderate = async (row, status) => {
    setBusyId(row.id);
    setError("");
    try {
      await updateDoc(doc(db, "students", row.studentId, "comments", row.id), {
        status,
        reviewedAt: serverTimestamp(),
        reviewedBy: auth.currentUser?.uid || null,
        reviewedByEmail: auth.currentUser?.email || null,
      });
      setComments((prev) => prev.filter((c) => c.id !== row.id));
    } catch (err) {
      setError(err.message || "Failed to update comment");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <h1 className={styles.title}>Comment moderation</h1>
      <p className={styles.lead}>
        Approve comments before students and teachers can see them. Rejected
        comments are hidden from everyone else.
      </p>

      <div className={styles.panel}>
        <div className={styles.row}>
          <button type="button" className={styles.button} onClick={loadPending}>
            Refresh
          </button>
        </div>
        {error && <p className={styles.error}>{error}</p>}
        {loading ? (
          <p className={styles.muted}>Loading…</p>
        ) : comments.length === 0 ? (
          <p className={styles.muted}>No pending comments.</p>
        ) : (
          comments.map((row) => (
            <div key={row.path} className={styles.card}>
              <strong>{row.text}</strong>
              <div className={styles.meta}>
                <span>Student: {row.studentId}</span>
                <span>
                  Teacher: {row.teacherName || "—"} ({row.teacherId})
                </span>
                <span>{formatWhen(row.createdAt)}</span>
              </div>
              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.buttonOk}
                  disabled={busyId === row.id}
                  onClick={() => moderate(row, "approved")}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className={styles.buttonDanger}
                  disabled={busyId === row.id}
                  onClick={() => moderate(row, "rejected")}
                >
                  Reject
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
